import React, { useState, useEffect } from 'react';
import { Header } from './Header';
import { Sidebar } from './Sidebar';
import { RightSidebar } from './RightSidebar';
import { TimesheetForm } from './TimesheetForm';
import { LeaveRequestForm } from './LeaveRequestForm';
import { OfflineBanner } from './OfflineBanner';
import { OnlineSyncModal } from './OnlineSyncModal';
import { OfflineBlock } from './OfflineBlock';
import { ProfileSetupModal } from './ProfileSetupModal';
import { MessengerManager } from './MessengerManager';
import { ThemeProvider } from '../contexts/ThemeContext';
import { BreadcrumbProvider } from '../contexts/BreadcrumbContext';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../i18n/hooks/useTranslation';
import { logger } from '../utils/logger';
import { offlineDataCache, CACHE_KEYS } from '../utils/offlineDataCache';

interface LayoutProps {
  children: React.ReactNode;
}

type PaneType = 'ai' | 'notifications' | 'messaging' | null;

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user } = useAuth();
  const { isRTL } = useTranslation();
  // Load sidebar state from localStorage
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved === 'true';
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activePane, setActivePane] = useState<PaneType>(null);
  const [showTimesheetForm, setShowTimesheetForm] = useState(false);
  const [showLeaveForm, setShowLeaveForm] = useState(false);
  const [leaveFormKey, setLeaveFormKey] = useState(0);
  const [staff, setStaff] = useState<any[]>([]);
  const [selectedStaffForTimesheet, setSelectedStaffForTimesheet] = useState<string[]>([]);
  const [preselectedDate, setPreselectedDate] = useState<Date | null>(null);
  const [editingTimesheetId, setEditingTimesheetId] = useState<string | null>(null);
  const [prefilledTimeEntries, setPrefilledTimeEntries] = useState<any[] | null>(null);
  const [preselectedProjectId, setPreselectedProjectId] = useState<string | null>(null);
  const [preselectedTaskId, setPreselectedTaskId] = useState<string | null>(null);

  const isSystemAdmin = user?.role === 'System Administrator';
  const showAdminBanner = isSystemAdmin && !user?.impersonating;
  const showImpersonationBanner = user?.impersonating;

  // Debug: Log impersonation state
  console.log('[Layout] Impersonation state:', {
    impersonating: user?.impersonating,
    showImpersonationBanner,
    userEmail: user?.email,
    originalAdmin: (user as any)?.originalAdmin
  });

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  // Fetch staff on mount
  useEffect(() => {
    fetchStaff();
  }, []);

  const fetchStaff = async () => {
    // Check cache first (for offline support)
    const cached = offlineDataCache.get<any[]>(CACHE_KEYS.STAFF);
    if (cached) {
      logger.log('[Layout] Using cached staff:', cached.length);
      setStaff(cached);
    }

    // If online, try to fetch fresh data
    if (navigator.onLine) {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/api/staff`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (data.success) {
          const staffData = data.data || [];
          // Cache the fresh data
          offlineDataCache.set(CACHE_KEYS.STAFF, staffData);
          setStaff(staffData);
      }
    } catch (error) {
      logger.error('Error fetching staff:', error);
        // If fetch fails and we don't have cached data, keep empty array
        if (!cached) {
          setStaff([]);
        }
      }
    } else {
      // Offline and no cache - keep empty array
      if (!cached) {
        logger.warn('[Layout] Offline and no cached staff available');
        setStaff([]);
      }
    }
  };

  const handleOpenTimesheetForm = (event?: any) => {
    logger.log('[LAYOUT] Received openTimesheetForm event - opening form');
    logger.log('[LAYOUT] Staff count:', staff.length);
    logger.log('[LAYOUT] Event detail:', event?.detail);
    
    // Check if event has preselected staff and date
    if (event?.detail?.staffId) {
      const staffId = event.detail.staffId;
      logger.log('[LAYOUT] Pre-selecting staff from event:', staffId);
      logger.log('[LAYOUT] Staff object:', staff.find(s => s._id === staffId));
      setSelectedStaffForTimesheet([staffId]);
      // Clear any group selections since we're selecting an individual
      setSelectedStaffForTimesheet([staffId]);
    } else {
      // Get current user's staff record and set as default (same logic as Timesheets component)
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const currentUser = JSON.parse(userStr);
        
        // Try multiple matching strategies
        let currentUserStaff = null;
        
        // Strategy 1: Match by userId
        if (currentUser.userId) {
          currentUserStaff = staff.find((s: any) => s.userId && s.userId === currentUser.userId);
        }
        
        // Strategy 2: Match by email
        if (!currentUserStaff && currentUser.email) {
          currentUserStaff = staff.find((s: any) => s.email && s.email.toLowerCase() === currentUser.email.toLowerCase());
        }
        
        // Strategy 3: Match by firstName AND lastName
        if (!currentUserStaff && currentUser.firstName && currentUser.lastName) {
          currentUserStaff = staff.find((s: any) => 
            s.firstName && s.lastName &&
            s.firstName.toLowerCase() === currentUser.firstName.toLowerCase() &&
            s.lastName.toLowerCase() === currentUser.lastName.toLowerCase()
          );
        }
        
        // Strategy 4: Match by user._id against staff.userId
        if (!currentUserStaff && currentUser._id) {
          currentUserStaff = staff.find((s: any) => s.userId && s.userId === currentUser._id);
        }
        
        if (currentUserStaff) {
          logger.log('[LAYOUT] Pre-selecting staff:', currentUserStaff.firstName, currentUserStaff.lastName);
          setSelectedStaffForTimesheet([currentUserStaff._id]);
        } else {
          logger.log('[LAYOUT] No staff match found for current user');
          setSelectedStaffForTimesheet([]);
        }
      }
    }
    
    // Set preselected date if provided
    if (event?.detail?.date) {
      logger.log('[LAYOUT] Pre-selecting date from event:', event.detail.date);
      setPreselectedDate(event.detail.date);
    } else {
      setPreselectedDate(null);
    }
    
    // Set editing timesheet ID if provided
    if (event?.detail?.timesheetId) {
      logger.log('[LAYOUT] Editing timesheet ID:', event.detail.timesheetId);
      setEditingTimesheetId(event.detail.timesheetId);
    } else {
      setEditingTimesheetId(null);
    }
    
    // 🔥 Set AI-generated time entries if provided
    if (event?.detail?.timeEntries && Array.isArray(event.detail.timeEntries)) {
      logger.log('[LAYOUT] 🕐 Pre-filling time entries from AI:', event.detail.timeEntries);
      setPrefilledTimeEntries(event.detail.timeEntries);
    } else {
      setPrefilledTimeEntries(null);
    }
    
    // Set preselected project ID if provided
    if (event?.detail?.projectId) {
      logger.log('[LAYOUT] Pre-selecting project ID from event:', event.detail.projectId);
      setPreselectedProjectId(event.detail.projectId);
    } else {
      setPreselectedProjectId(null);
    }
    
    // Set preselected task ID if provided
    if (event?.detail?.taskId) {
      logger.log('[LAYOUT] Pre-selecting task ID from event:', event.detail.taskId);
      setPreselectedTaskId(event.detail.taskId);
    } else {
      setPreselectedTaskId(null);
    }
    
    setShowTimesheetForm(true);
  };

  const handleOpenLeaveForm = (event?: CustomEvent) => {
    logger.log('[LAYOUT] Opening leave request form', event?.detail);
    setLeaveFormKey(Date.now()); // Force remount with fresh key
    setShowLeaveForm(true);
    // Store the date detail for the form to use
    if (event?.detail?.date) {
      (window as any).__pendingLeaveFormDate = event.detail.date;
    } else {
      delete (window as any).__pendingLeaveFormDate;
    }
  };

  // Listen for dashboard widget actions
  useEffect(() => {
    const handleOpenRightPane = (event: CustomEvent<PaneType>) => {
      logger.log('[LAYOUT] Received openRightPane event:', event.detail);
      setActivePane(event.detail);
    };

    const handleTimesheetDeleted = (e: CustomEvent<{ timesheetIds?: string[] }>) => {
      const deletedIds = e.detail?.timesheetIds ?? [];
      if (editingTimesheetId && deletedIds.includes(editingTimesheetId)) {
        logger.log('[Layout] Timesheet being edited was deleted - closing form');
        setShowTimesheetForm(false);
        setSelectedStaffForTimesheet([]);
        setPreselectedDate(null);
        setEditingTimesheetId(null);
        setPrefilledTimeEntries(null);
        setPreselectedProjectId(null);
        setPreselectedTaskId(null);
      }
    };

    window.addEventListener('openRightPane' as any, handleOpenRightPane);
    window.addEventListener('openTimesheetForm' as any, handleOpenTimesheetForm);
    window.addEventListener('openLeaveForm' as any, handleOpenLeaveForm);
    window.addEventListener('timesheetDeleted' as any, handleTimesheetDeleted);

    return () => {
      window.removeEventListener('openRightPane' as any, handleOpenRightPane);
      window.removeEventListener('openTimesheetForm' as any, handleOpenTimesheetForm);
      window.removeEventListener('openLeaveForm' as any, handleOpenLeaveForm);
      window.removeEventListener('timesheetDeleted' as any, handleTimesheetDeleted);
    };
  }, [staff, editingTimesheetId]);

  return (
    <ThemeProvider>
      <BreadcrumbProvider>
        {/* Profile Setup Modal - blocks app until profile is complete */}
        <ProfileSetupModal />
        {/* Offline Banner */}
        <OfflineBlock />
        <OfflineBanner />
        {/* Online Sync Modal */}
        <OnlineSyncModal />
        {/* Messenger Manager - handles all messenger popup windows */}
        <MessengerManager />
        <div 
          className={`flex h-screen bg-gray-50 dark:bg-gray-900`}
          style={{ 
            paddingTop: `calc(var(--offline-banner-height, 0px) + ${showAdminBanner || showImpersonationBanner ? '44px' : '0px'})`,
            transition: 'padding-top 0.2s ease-in-out'
          }}
        >
        {/* Left Sidebar - Desktop always visible, Mobile hamburger overlay */}
        <Sidebar 
          collapsed={sidebarCollapsed} 
          onToggle={() => {
            const newState = !sidebarCollapsed;
            setSidebarCollapsed(newState);
            localStorage.setItem('sidebarCollapsed', String(newState));
            // Dispatch custom event for other components to listen
            window.dispatchEvent(new CustomEvent('sidebarToggle', { detail: newState }));
          }}
          mobileMenuOpen={mobileMenuOpen}
          onMobileMenuToggle={() => setMobileMenuOpen(!mobileMenuOpen)}
        />
        
        {/* Mobile Menu Overlay */}
        <div 
          className={`
            fixed inset-0 bg-black z-30 md:hidden
            transition-opacity duration-300 ease-in-out
            ${mobileMenuOpen ? 'opacity-50 pointer-events-auto' : 'opacity-0 pointer-events-none'}
          `}
          onClick={() => setMobileMenuOpen(false)}
          aria-hidden="true"
        />
        
        {/* Main Content Area */}
        <div className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${activePane ? (isRTL ? 'md:ml-96' : 'md:mr-96') : ''}`}>
          {/* Header */}
          <Header onMobileMenuToggle={() => setMobileMenuOpen(!mobileMenuOpen)} />
          
          {/* Main Content - min-h-0 allows flex child to shrink; pb-20 on mobile clears fixed bottom nav */}
          <main className="flex-1 min-h-0 overflow-auto pb-20 md:pb-0">
            <div className="min-h-full">
              {children}
            </div>
          </main>
        </div>
        
        {/* Right Sidebar with Panes */}
        <RightSidebar 
          activePane={activePane}
          setActivePane={setActivePane}
          onNewTimesheet={handleOpenTimesheetForm}
        />

        {/* Global Timesheet Form Modal */}
        {showTimesheetForm && (
          <TimesheetForm
            onClose={() => {
              setShowTimesheetForm(false);
              setSelectedStaffForTimesheet([]);
              setPreselectedDate(null);
              setEditingTimesheetId(null);
              setPrefilledTimeEntries(null);
              setPreselectedProjectId(null);
              setPreselectedTaskId(null);
            }}
            onSave={(detail) => {
              setShowTimesheetForm(false);
              setSelectedStaffForTimesheet([]);
              setPreselectedDate(null);
              setEditingTimesheetId(null);
              setPrefilledTimeEntries(null);
              setPreselectedProjectId(null);
              setPreselectedTaskId(null);
              // Dispatch event to refresh timesheet lists (pass saved dates so list can expand date range if needed)
              window.dispatchEvent(new CustomEvent('timesheetSaved', { detail: detail || {} }));
            }}
            selectedStaff={selectedStaffForTimesheet}
            onStaffChange={setSelectedStaffForTimesheet}
            staffList={staff}
            preselectedDate={preselectedDate}
            aiTimeEntries={prefilledTimeEntries}
            preselectedProjectId={preselectedProjectId}
            preselectedTaskId={preselectedTaskId}
            editingTimesheet={editingTimesheetId ? { _id: editingTimesheetId } : undefined}
          />
        )}

        {/* Global Leave Request Form Modal */}
        {showLeaveForm && (
          <LeaveRequestForm
            key={`leave-form-${leaveFormKey}`}
            onClose={() => {
              console.log('[Layout] LeaveRequestForm onClose called');
              setShowLeaveForm(false);
              delete (window as any).__pendingLeaveFormDate;
            }}
            onSuccess={() => {
              console.log('[Layout] LeaveRequestForm onSuccess called - dispatching leaveRequestSaved event');
              // Dispatch event to refresh leave request lists
              window.dispatchEvent(new CustomEvent('leaveRequestSaved'));
              console.log('[Layout] leaveRequestSaved event dispatched');
            }}
          />
        )}

      </div>
      </BreadcrumbProvider>
    </ThemeProvider>
  );
};
